import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, Users, Car, StickyNote, MessageCircle, Armchair } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { RouteLine, AcceptsIcons } from "@/components/Cards";
import { UserIdentity } from "@/components/UserIdentity";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function TripDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [showOrig, setShowOrig] = useState(false);
  const [parcelDialog, setParcelDialog] = useState(false);
  const [myParcels, setMyParcels] = useState([]);

  useEffect(() => {
    api.get(`/trips/${id}`, { params: { lang } }).then((r) => setTrip(r.data)).catch(() => setTrip(false));
  }, [id, lang]);

  if (trip === null) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.loading")}</div>;
  if (trip === false) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.error")}</div>;

  const requireAuth = (fn) => () => {
    if (!user) {
      toast.info(t("auth.loginRequired"));
      navigate(`/entrar?next=/viagem/${id}`);
      return;
    }
    fn();
  };

  const contact = async () => {
    try {
      const { data } = await api.post("/conversations", { user_id: trip.driver_id, trip_id: trip.id });
      navigate(`/mensagens/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const bookRide = async () => {
    try {
      await api.post("/bookings", { trip_id: trip.id, type: "ride", seats: 1 });
      toast.success(t("trip.booked"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openParcelBooking = async () => {
    try {
      const { data } = await api.get("/my/parcels", { params: { trip_id: trip.id } });
      setMyParcels(data);
      setParcelDialog(true);
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const bookParcel = async (parcelId) => {
    try {
      await api.post("/bookings", { trip_id: trip.id, type: "parcel", parcel_id: parcelId });
      toast.success(t("trip.booked"));
      setParcelDialog(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const isOwn = user && user.id === trip.driver_id;
  const available = trip.seats - (trip.seats_taken || 0);
  const acceptsLabel = { passengers: t("trip.acceptsPassengers"), parcels: t("trip.acceptsParcels"), both: t("trip.acceptsBoth") }[trip.accepts];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="trip-detail-page">
      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-10 shadow-sm">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-primary">{trip.origin} → {trip.destination}</h1>
            <Badge className="bg-secondary text-primary hover:bg-secondary" data-testid="trip-accepts-badge">{acceptsLabel}</Badge>
            {trip.status === "completed" && <Badge variant="secondary" data-testid="trip-completed-badge">{t("trip.completed")}</Badge>}
          </div>
          <div className="mb-8">
            <p className="text-sm font-semibold text-slate-500 mb-3">{t("trip.route")}</p>
            {trip.percurso ? (
              <p className="text-base font-medium text-primary" data-testid="trip-percurso">{showOrig ? trip.percurso : (trip._tr?.percurso || trip.percurso)}</p>
            ) : (
              <RouteLine origin={trip.origin} stops={trip.stops} destination={trip.destination} />
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-8">
            <div className="flex items-center gap-2 text-slate-600"><Calendar size={16} className="text-accent" />{trip.date}</div>
            {trip.time && <div className="flex items-center gap-2 text-slate-600"><Clock size={16} className="text-accent" />{trip.time}</div>}
            {trip.accepts !== "parcels" && <div className="flex items-center gap-2 text-slate-600"><Armchair size={16} className="text-accent" />{available} {t("trip.seats")}</div>}
            {trip.vehicle && <div className="flex items-center gap-2 text-slate-600"><Car size={16} className="text-accent" />{trip.vehicle}</div>}
          </div>
          {trip.notes && (
            <div className="bg-slate-50 rounded-xl p-5 text-sm text-slate-600 flex gap-3">
              <StickyNote size={16} className="text-slate-400 shrink-0 mt-0.5" />
              <div>
                <p>{showOrig ? trip.notes : (trip._tr?.notes || trip.notes)}</p>
                {(trip._tr?.notes || trip._tr?.percurso) && (
                  <button type="button" data-testid="trip-original-toggle" onClick={() => setShowOrig(!showOrig)} className="text-xs text-accent font-semibold mt-2 underline underline-offset-2">{showOrig ? t("translate.showTranslation") : t("translate.showOriginal")}</button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="driver-card">
            <p className="text-sm font-semibold text-slate-500 mb-4">{t("trip.driver")}</p>
            <UserIdentity user={trip.driver} avatarSize="w-12 h-12" avatarText="text-lg" nameClass="text-base font-semibold text-primary" testid="driver-profile-link" avatarTestid="driver-avatar" />
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xl font-bold text-primary mb-1">{trip.price > 0 ? `${trip.price}€ ${t("trip.price")}` : t("trip.toArrange")}</p>
              <p className="text-xs text-slate-400">{t("chat.privacy")}</p>
            </div>
          </div>

          {user && !isOwn && trip.status === "active" && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm flex flex-col gap-3">
              {(trip.accepts === "passengers" || trip.accepts === "both") && available > 0 && (
                <Button data-testid="book-ride-button" onClick={requireAuth(bookRide)} className="rounded-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold">
                  <Users size={16} className="mr-2" />{t("trip.book")}
                </Button>
              )}
              {(trip.accepts === "parcels" || trip.accepts === "both") && (
                <Button data-testid="book-parcel-button" onClick={requireAuth(openParcelBooking)} variant="outline" className="rounded-full h-12 border-accent text-accent hover:bg-accent/5 font-semibold">
                  {t("trip.bookParcel")}
                </Button>
              )}
              <Button data-testid="contact-driver-button" onClick={requireAuth(contact)} variant="outline" className="rounded-full h-12 font-semibold">
                <MessageCircle size={16} className="mr-2" />{t("trip.contact")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={parcelDialog} onOpenChange={setParcelDialog}>
        <DialogContent className="rounded-2xl" data-testid="parcel-select-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">{t("trip.selectParcel")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto overscroll-contain pr-1" data-testid="parcel-select-list">
            {myParcels.length === 0 && (
              <div className="text-sm text-slate-500">
                {t("trip.noParcels")}{" "}
                <Link to="/enviar-encomenda" data-testid="create-parcel-link" className="text-accent font-semibold hover:underline">{t("nav.sendParcel")}</Link>
              </div>
            )}
            {myParcels.map((p) => (
              <button key={p.id} data-testid={`select-parcel-${p.id}`} onClick={() => bookParcel(p.id)} className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-xl p-4 transition-colors duration-200">
                <p className="font-semibold text-primary text-sm">{p.origin} → {p.destination}</p>
                <p className="text-xs text-slate-500 mt-1" data-testid={`select-parcel-dates-${p.id}`}>{t("parcel.departureDate")}: {p.departure_date || "—"} · {t("parcel.deliverBy")} {p.date}</p>
                <p className="text-xs text-slate-500 mt-1">{p.description}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
